import { homePath } from "@/lib/roles";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { LogOut, Rocket, GraduationCap, Gamepad2, BarChart3, Volume2, VolumeX, Coins, ShoppingBag } from "lucide-react";
import sounds from "@/lib/sounds";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [soundOn, setSoundOn] = useState(true);

  const handleLogout = async () => {
    const wasAdmin = user?.role === "admin";
    await logout();
    navigate(wasAdmin ? "/admin" : "/login");
  };

  const isActive = (path) => location.pathname === path;

  const studentLinks = [
    { path: "/student", label: "Dashboard", icon: Rocket },
    { path: "/practice", label: "Practice", icon: GraduationCap },
    { path: "/shop", label: "Shop", icon: ShoppingBag },
  ];

  const teacherLinks = [
    { path: "/teacher", label: "Dashboard", icon: BarChart3 },
  ];

  const adminLinks = [
    { path: "/admin/dashboard", label: "School Overview", icon: BarChart3 },
  ];

  const links = user?.role === "admin" ? adminLinks : user?.role === "teacher" ? teacherLinks : studentLinks;

  return (
    <nav
      className="sticky top-0 z-50 glass-surface px-6 py-3 flex items-center justify-between"
      data-testid="main-navbar"
    >
      <div className="flex items-center gap-8">
        <button
          onClick={() => navigate(homePath(user?.role))}
          className="flex items-center gap-2 group"
          data-testid="nav-logo"
        >
          <Gamepad2 className="w-6 h-6 text-cyber" />
          <span className="font-heading text-lg font-bold tracking-tight text-white group-hover:text-cyber transition-colors duration-200">
            MathBlaster
          </span>
        </button>

        <div className="flex items-center gap-1">
          {links.map(({ path, label, icon: Icon }) => (
            <button
              key={path}
              onClick={() => navigate(path)}
              data-testid={`nav-${label.toLowerCase()}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-body font-medium transition-colors duration-200 ${
                isActive(path)
                  ? "bg-cyber/10 text-cyber"
                  : "text-white/60 hover:text-white hover:bg-white/5"
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-4">
        {user?.role === "student" && (
          <button
            onClick={() => navigate("/shop")}
            data-testid="nav-coins"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-yellow-500/10 hover:bg-yellow-500/20 transition-colors duration-200"
          >
            <Coins className="w-3.5 h-3.5 text-yellow-400" />
            <span className="font-mono text-sm font-bold text-yellow-400">{user?.coins || 0}</span>
          </button>
        )}
        <button
          onClick={() => { const on = sounds.toggle(); setSoundOn(on); }}
          data-testid="sound-toggle-btn"
          className={`p-1.5 rounded-full transition-colors duration-200 ${
            soundOn ? "text-cyber hover:bg-cyber/10" : "text-white/30 hover:bg-white/5"
          }`}
          title={soundOn ? "Mute sounds" : "Unmute sounds"}
        >
          {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </button>
        <span className="text-sm text-white/50 font-body" data-testid="nav-user-name">
          {user?.name}
        </span>
        <span className="text-xs font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 text-white/40">
          {user?.role}
        </span>
        <button
          onClick={handleLogout}
          data-testid="nav-logout-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm text-white/50 hover:text-magenta hover:bg-magenta/10 transition-colors duration-200"
        >
          <LogOut className="w-4 h-4" />
          Exit
        </button>
      </div>
    </nav>
  );
}
